import type { ItemLegacyDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { differenceInDays, parse } from 'date-fns';
import { useMemo } from 'react';
import type { ItemV2 } from '@/common/api/types/items';
import type {
  IVisualizationHeader,
  IVisualizationHeaderProps,
} from '@/common/api/types/loopVisualization';
import { dateToUTCMidnight } from '@/common/utils/dateFormats';
import { DEFAULT_FALLBACK_COLOR, SUBHEADER_OPACITY } from '@/features/visualizations/utils/colors';
import { COMPOSITE_ID_SEPARATOR, NO_VALUE_ID } from '../utils/constants';
import { getHeaderFieldId, getHeaderItemKey } from '../utils/visualizationHeaderUtils';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';
import {
  buildTimelineDateAxis,
  flattenTimelineDateAxisToRanges,
  getNormalizePlannedDates,
} from './utils/timelineDateUtils';
import {
  calculateChildLanes,
  calculateMaxLanesForOrder,
  stackTimelineItems,
} from './utils/timelineStackingUtils';

const flattenItems = (items: { [key: string]: TimelineItemType[] }): TimelineItemType[] => {
  const result = Object.values(items).flat();
  result.sort((a, b) => (a.order || 0) - (b.order || 0));
  return result;
};

type TimeRange = {
  start: Date;
  end: Date;
  key: string;
};

type ItemsByPropertyKeyAndDate = {
  [key: string]: { [key: string]: TimelineItemType[] };
};

type HighestOrderByPropertyKey = {
  [key: string]: number;
};

type SubRowData = {
  id: string;
  items: { [key: string]: TimelineItemType[] };
  maxOrder: number;
};

type ProcessItemsResult = {
  itemsByPropertyKeyAndDate: ItemsByPropertyKeyAndDate;
  highestOrderByPropertyKey: HighestOrderByPropertyKey;
  longestItemDurationInDays: number;
  subRowsByPropertyKey?: { [key: string]: SubRowData[] };
  effectiveSubHeaders?: IVisualizationHeaderProps[];
};
type RecordValue<T> = T extends Record<string, infer V> ? V : never;

type CustomFieldValue = RecordValue<NonNullable<ItemLegacyDTO['customFields']>>;

type Normalized = string | number | boolean | { year: number; month: number; day: number };

const extractCustomFieldValues = (
  values: ItemV2['customFieldValues'] | undefined,
  fieldId: string,
): Normalized[] => {
  if (!values) return [];

  const raw = values[fieldId];
  if (raw == null) return [];

  const unwrap = (val: CustomFieldValue): Normalized[] => {
    if (val == null) return [];

    if (Array.isArray(val)) {
      return val.flatMap((v) => unwrap(v as CustomFieldValue));
    }

    if (typeof val === 'object') {
      if ('value' in val) {
        return unwrap(val.value as CustomFieldValue);
      }

      if ('year' in val || 'month' in val || 'day' in val) {
        const { year, month, day } = val;
        if (year !== undefined && month !== undefined && day !== undefined) {
          return [{ year, month, day }];
        }
      }

      if ('id' in val) {
        const obj = val as Record<string, unknown>;
        const fieldType = obj.fieldType;
        const typeKey =
          typeof fieldType === 'string' &&
          fieldType !== 'id' &&
          fieldType !== 'fieldType' &&
          Object.hasOwn(obj, fieldType)
            ? fieldType
            : Object.keys(obj).find((k) => k !== 'id' && k !== 'fieldType');
        if (typeKey !== undefined) {
          const entryValue = obj[typeKey];
          if (
            typeof entryValue === 'string' ||
            typeof entryValue === 'number' ||
            typeof entryValue === 'boolean'
          ) {
            return [entryValue];
          }
        }
      }

      return [];
    }

    return [val];
  };

  return unwrap(raw as CustomFieldValue);
};

const groupItemByCustomField = (
  item: TimelineItemType,
  fieldId: string,
  map: { [key: string]: TimelineItemType[] },
): boolean => {
  const values = extractCustomFieldValues(item.customFieldValues, fieldId);

  if (!values.length) {
    if (!map.null) {
      map.null = [];
    }
    map.null.push(item);
    return true;
  }

  const toKey = (v: Normalized): string => {
    if (typeof v === 'object') {
      return `${v.year}-${String(v.month).padStart(2, '0')}-${String(v.day).padStart(2, '0')}`;
    }
    return v.toString();
  };

  for (const v of new Set(values.map(toKey))) {
    const key = v || NO_VALUE_ID;
    if (!map[key]) map[key] = [];
    map[key].push(item);
  }

  return true;
};

const groupItemByCustomFieldCombined = (
  item: TimelineItemType,
  fieldId: string,
  map: { [key: string]: TimelineItemType[] },
): void => {
  const values = extractCustomFieldValues(item.customFieldValues, fieldId);

  if (!values.length) {
    if (!map[NO_VALUE_ID]) map[NO_VALUE_ID] = [];
    map[NO_VALUE_ID].push(item);
    return;
  }

  const toKey = (v: Normalized): string => {
    if (typeof v === 'object') {
      return `${v.year}-${String(v.month).padStart(2, '0')}-${String(v.day).padStart(2, '0')}`;
    }
    return v.toString();
  };

  const uniqueKeys = [...new Set(values.map(toKey))].sort();
  const compositeKey = uniqueKeys.join(COMPOSITE_ID_SEPARATOR) || NO_VALUE_ID;
  if (!map[compositeKey]) map[compositeKey] = [];
  map[compositeKey].push(item);
};

const groupItemsByPropertyKey = (
  items: TimelineItemType[],
  propertyKey: string | null | undefined,
  map: { [key: string]: TimelineItemType[] } = {},
  combineMultiSelect = false,
): { [key: string]: TimelineItemType[] } => {
  for (const item of items) {
    if (propertyKey?.startsWith('customFields.')) {
      const [, fieldId] = propertyKey.split('.') as ['customFields', string];
      if (combineMultiSelect) {
        groupItemByCustomFieldCombined(item, fieldId, map);
      } else {
        groupItemByCustomField(item, fieldId, map);
      }
      continue;
    }
    const value = propertyKey ? item[propertyKey as keyof TimelineItemType] : NO_VALUE_ID;
    const key = value?.toString() || NO_VALUE_ID;
    if (!map[key]) {
      map[key] = [];
    }
    map[key].push(item);
  }
  return map;
};

export const buildCombinedMainHeaders = (
  existingHeaders: IVisualizationHeaderProps[],
  itemsMapByPropertyKey: { [key: string]: TimelineItemType[] },
): IVisualizationHeaderProps[] => {
  const headerById = new Map(existingHeaders.map((h) => [h.id?.toString() ?? '', h]));
  const visibleIds = new Set(existingHeaders.map((h) => h.id?.toString() ?? ''));
  const result: IVisualizationHeaderProps[] = [];
  const seen = new Set<string>();

  for (const header of existingHeaders) {
    const key = header.id?.toString() ?? '';
    if (!seen.has(key)) {
      seen.add(key);
      result.push(header);
    }
  }

  for (const compositeKey of Object.keys(itemsMapByPropertyKey)) {
    if (seen.has(compositeKey)) continue;
    seen.add(compositeKey);

    const parts = compositeKey.split(COMPOSITE_ID_SEPARATOR);
    if (!parts.some((p) => visibleIds.has(p))) continue;

    const labels = parts.map((p) => headerById.get(p)?.label ?? p).filter(Boolean);
    const firstMatch = parts.map((p) => headerById.get(p)).find(Boolean);
    const displayValues = parts.flatMap((p) => headerById.get(p)?.displayValues ?? []);

    result.push({
      id: compositeKey,
      label: labels.join(', '),
      displayValues: displayValues.length > 0 ? displayValues : undefined,
      selector: firstMatch?.selector ?? {
        type: 'CustomPropertySelectorByID',
        customPropertyID: parts[0] ?? compositeKey,
      },
      headerColor: firstMatch?.headerColor || DEFAULT_FALLBACK_COLOR,
      isDateHeader: false,
    });
  }

  return result;
};

const applySubRowOrdering = (
  subRowsData: SubRowData[],
  subHeaderConfig: { orderedHeaderIds: string[]; hiddenHeaderIds: string[] } | undefined,
): SubRowData[] => {
  if (!subHeaderConfig) return subRowsData;

  const hiddenSet = new Set(subHeaderConfig.hiddenHeaderIds);
  const orderedIds = subHeaderConfig.orderedHeaderIds;

  if (!orderedIds.length && !hiddenSet.size) return subRowsData;

  const byId = new Map(subRowsData.map((sr) => [sr.id, sr]));
  const orderedSet = new Set(orderedIds);

  const result: SubRowData[] = [];
  for (const id of orderedIds) {
    if (!hiddenSet.has(id)) {
      const sr = byId.get(id);
      if (sr) result.push(sr);
    }
  }
  for (const sr of subRowsData) {
    if (!orderedSet.has(sr.id) && !hiddenSet.has(sr.id)) {
      result.push(sr);
    }
  }
  return result;
};

export const calculateRowHeightAndMaxDepth = (
  itemsByDate: { [key: string]: TimelineItemType[] },
  expandedChildren: { [key: number]: boolean },
  laneHeight: number,
  childLanesCache?: Map<number, number>,
  visibleWindow?: { from: Date | null; to: Date | null },
): { calculatedHeight: number; maxChildDepth: number } => {
  const cache = childLanesCache ?? new Map<number, number>();
  const itemsByOrder = new Map<number, TimelineItemType[]>();
  let maxChildDepth = 0;

  // Only items whose dates intersect the visible window count towards row
  // height; items stacked entirely outside [from, to] still live in the
  // timeMap (the minimap needs them) but must not inflate the row.
  const overlapsWindow = (item: TimelineItemType): boolean => {
    if (!visibleWindow?.from && !visibleWindow?.to) return true;
    const { start, finish } = getNormalizePlannedDates(
      item.plannedStartDateTime,
      item.plannedFinishDateTime,
    );
    const itemStart = start ? dateToUTCMidnight(parse(start, 'yyyy-MM-dd', new Date())) : null;
    const itemEnd = finish ? dateToUTCMidnight(parse(finish, 'yyyy-MM-dd', new Date())) : null;
    if (!itemStart || !itemEnd) return true;
    if (visibleWindow.to && itemStart > visibleWindow.to) return false;
    if (visibleWindow.from && itemEnd < visibleWindow.from) return false;
    return true;
  };

  for (const items of Object.values(itemsByDate)) {
    for (const item of items) {
      if (!overlapsWindow(item)) continue;
      const order = item.order ?? 0;
      if (!itemsByOrder.has(order)) itemsByOrder.set(order, []);
      itemsByOrder.get(order)?.push(item);

      if (expandedChildren[item.id]) {
        const depth = calculateChildLanes(item, cache);
        if (depth > maxChildDepth) maxChildDepth = depth;
      }
    }
  }

  // Orders are assigned globally, so an in-scope item can sit at a high order
  // while lower orders are out of scope. Height must cover every lane up to the
  // highest in-scope order, treating gaps as single empty lanes.
  let totalLanes = 0;
  if (itemsByOrder.size > 0) {
    const maxOrder = Math.max(...itemsByOrder.keys());
    for (let order = 0; order <= maxOrder; order++) {
      const itemsAtOrder = itemsByOrder.get(order) ?? [];
      totalLanes += calculateMaxLanesForOrder(itemsAtOrder, expandedChildren, cache);
    }
  }

  return {
    calculatedHeight: Math.max(totalLanes * laneHeight, laneHeight),
    maxChildDepth,
  };
};

const findRangeByBinarySearch = (
  timeRanges: TimeRange[],
  targetStart: Date,
): TimeRange | undefined => {
  let lo = 0;
  let hi = timeRanges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const range = timeRanges[mid];
    if (!range) break;
    if (targetStart > range.end) {
      lo = mid + 1;
    } else if (targetStart < range.start) {
      hi = mid - 1;
    } else {
      return range;
    }
  }
  return undefined;
};

const mapItemsToTimeRanges = ({
  from,
  stackedItems,
  timeRanges,
}: {
  from: string;
  stackedItems: TimelineItemType[];
  timeRanges: TimeRange[];
}): {
  timeMap: { [key: string]: TimelineItemType[] };
  longestItemDuration: number;
} => {
  const timeMap: { [key: string]: TimelineItemType[] } = {};
  for (const range of timeRanges) {
    timeMap[range.key] = [];
  }

  if (timeRanges.length === 0) {
    return { timeMap, longestItemDuration: 0 };
  }

  let longestItemDuration = 0;
  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const firstRangeInScope = timeRanges.find((r) => r.end >= fromDate) ?? timeRanges[0];
  const lastRange = timeRanges[timeRanges.length - 1];

  for (const item of stackedItems) {
    const normalizedDates = getNormalizePlannedDates(
      item.plannedStartDateTime,
      item.plannedFinishDateTime,
    );
    if (!normalizedDates.hasAnyDate || !normalizedDates.start || !normalizedDates.finish) {
      continue;
    }

    const startDateString = normalizedDates.start;
    const endDateString = normalizedDates.finish;

    const itemTargetStart = dateToUTCMidnight(parse(startDateString, 'yyyy-MM-dd', new Date()));
    const itemEndDate = dateToUTCMidnight(parse(endDateString, 'yyyy-MM-dd', new Date()));

    if (!itemTargetStart || !itemEndDate || !fromDate) {
      continue;
    }

    let matchingRange: TimeRange | undefined;

    if (itemTargetStart < fromDate) {
      // Find the first range that ends after the fromDate
      matchingRange = firstRangeInScope;
    } else {
      matchingRange = findRangeByBinarySearch(timeRanges, itemTargetStart);
      if (!matchingRange) {
        matchingRange = lastRange;
      }
    }

    if (!matchingRange) {
      continue;
    }

    timeMap[matchingRange.key]?.push(item);
    const daysInItem = differenceInDays(itemEndDate, itemTargetStart) + 1;
    if (daysInItem > longestItemDuration) {
      longestItemDuration = daysInItem;
    }
  }

  return { timeMap, longestItemDuration };
};

const processItemsWithoutSubHeaders = ({
  from,
  itemsMapByPropertyKey,
  timeRanges,
  mainHeaders,
  showEmptyHeaders,
  expandedChildren,
  itemRanks,
}: {
  from: string;
  itemsMapByPropertyKey: { [key: string]: TimelineItemType[] };
  timeRanges: TimeRange[];
  mainHeaders: IVisualizationHeaderProps[];
  showEmptyHeaders: boolean;
  expandedChildren: { [key: number]: boolean };
  itemRanks?: Map<number, string>;
}): ProcessItemsResult => {
  const itemsByPropertyKeyAndDate: ItemsByPropertyKeyAndDate = {};
  const highestOrderByPropertyKey: HighestOrderByPropertyKey = {};
  let longestItemDurationInDays = 0;
  const childLanesCache = new Map<number, number>();

  for (const header of mainHeaders) {
    const key = header.id?.toString() || '';
    const items = itemsMapByPropertyKey[key] || [];
    if (!items.length && !showEmptyHeaders) {
      continue;
    }
    const { stackedItems, highestOrder } = stackTimelineItems(items, itemRanks);
    const { timeMap, longestItemDuration } = mapItemsToTimeRanges({
      from,
      stackedItems,
      timeRanges,
    });

    const { maxChildDepth } = calculateRowHeightAndMaxDepth(
      timeMap,
      expandedChildren,
      1,
      childLanesCache,
    );
    const effectiveMaxOrder = Math.max(highestOrder + 1, maxChildDepth) - 1;

    highestOrderByPropertyKey[key] = effectiveMaxOrder;

    if (longestItemDuration > longestItemDurationInDays) {
      longestItemDurationInDays = longestItemDuration;
    }

    itemsByPropertyKeyAndDate[key] = timeMap;
  }

  return { itemsByPropertyKeyAndDate, highestOrderByPropertyKey, longestItemDurationInDays };
};

const processItemsWithSubHeaders = ({
  from,
  itemsMapByPropertyKey,
  timeRanges,
  subHeaderPropertyKey,
  subHeaders,
  showEmptyHeaders,
  mainHeaders,
  expandedChildren,
  combineMultiSelect = false,
  itemRanks,
}: {
  from: string;
  itemsMapByPropertyKey: { [key: string]: TimelineItemType[] };
  timeRanges: TimeRange[];
  subHeaderPropertyKey: string;
  subHeaders: IVisualizationHeaderProps[];
  showEmptyHeaders: boolean;
  mainHeaders: IVisualizationHeaderProps[];
  expandedChildren: { [key: number]: boolean };
  combineMultiSelect?: boolean;
  itemRanks?: Map<number, string>;
}): ProcessItemsResult => {
  const subRowsByPropertyKey: { [key: string]: SubRowData[] } = {};
  const highestOrderByPropertyKey: { [key: string]: number } = {};
  const allEffectiveSubHeaders = new Map<string, IVisualizationHeaderProps>();
  let longestItemDurationInDays = 0;
  const childLanesCache = new Map<number, number>();

  for (const mainHeader of mainHeaders) {
    const key = mainHeader.id?.toString() || '';
    const items = itemsMapByPropertyKey[key] || [];
    const mainHeaderHasItems = items.length > 0;
    if (!mainHeaderHasItems && !showEmptyHeaders) {
      continue;
    }

    const itemsBySubHeader = groupItemsByPropertyKey(
      items,
      subHeaderPropertyKey,
      {},
      combineMultiSelect,
    );

    const effectiveSubHeadersForGroup = combineMultiSelect
      ? buildCombinedMainHeaders(subHeaders, itemsBySubHeader)
      : subHeaders;

    for (const h of effectiveSubHeadersForGroup) {
      if (!allEffectiveSubHeaders.has(h.id?.toString() ?? '')) {
        allEffectiveSubHeaders.set(h.id?.toString() ?? '', h);
      }
    }

    let highestOrderForParent = 0;
    const subRows: SubRowData[] = [];

    for (const subHeader of effectiveSubHeadersForGroup) {
      const subKey = subHeader.id?.toString() || '';
      const subItems = itemsBySubHeader[subKey] || [];

      const { stackedItems, highestOrder } = stackTimelineItems(subItems, itemRanks);

      const { timeMap, longestItemDuration } = mapItemsToTimeRanges({
        from,
        stackedItems,
        timeRanges,
      });

      const { maxChildDepth } = calculateRowHeightAndMaxDepth(
        timeMap,
        expandedChildren,
        1,
        childLanesCache,
      );
      const effectiveMaxOrder = Math.max(highestOrder + 1, maxChildDepth) - 1;

      highestOrderForParent = Math.max(highestOrderForParent, effectiveMaxOrder);

      if (longestItemDuration > longestItemDurationInDays) {
        longestItemDurationInDays = longestItemDuration;
      }

      if (showEmptyHeaders || subItems.length > 0) {
        subRows.push({
          id: subKey,
          items: timeMap,
          maxOrder: effectiveMaxOrder,
        });
      }
    }

    subRowsByPropertyKey[key] = subRows;
    highestOrderByPropertyKey[key] = highestOrderForParent;
  }

  return {
    itemsByPropertyKeyAndDate: {},
    highestOrderByPropertyKey,
    subRowsByPropertyKey,
    longestItemDurationInDays,
    effectiveSubHeaders: combineMultiSelect ? [...allEffectiveSubHeaders.values()] : undefined,
  };
};

export const useTimelineTableData = ({
  from,
  to,
  minimapStart,
  minimapEnd,
  items,
  timelineHeaderOptions,
  timelineSubHeaderOptions,
  mainHeaders,
  subHeaders,
  layout,
  showEmptyHeaders,
  expandedChildren,
  mainHeaderConfig,
  subHeaderConfigs,
  itemRanks,
}: {
  from: string;
  to: string;
  minimapStart: string;
  minimapEnd: string;
  items: TimelineItemType[] | undefined;
  timelineHeaderOptions: IVisualizationHeader | null;
  timelineSubHeaderOptions: IVisualizationHeader | null;
  mainHeaders: IVisualizationHeaderProps[];
  subHeaders?: IVisualizationHeaderProps[];
  layout: TimelineLayoutDTO | null;
  showEmptyHeaders: boolean;
  expandedChildren: { [key: number]: boolean };
  mainHeaderConfig?: { orderedHeaderIds: string[]; hiddenHeaderIds: string[] };
  subHeaderConfigs?: Record<string, { orderedHeaderIds: string[]; hiddenHeaderIds: string[] }>;
  itemRanks?: Map<number, string>;
}): TimelineRowType[] => {
  return useMemo(() => {
    if (mainHeaders.length === 0 || !layout) {
      return [];
    }

    const orderedMainHeaders = mainHeaderConfig?.orderedHeaderIds.length
      ? [
          ...mainHeaderConfig.orderedHeaderIds
            .map((id) => mainHeaders.find((h) => h.id?.toString() === id))
            .filter((h): h is IVisualizationHeaderProps => h !== undefined),
          ...mainHeaders.filter(
            (h) => !mainHeaderConfig.orderedHeaderIds.includes(h.id?.toString() || ''),
          ),
        ]
      : mainHeaders;

    const filteredMainHeaders = mainHeaderConfig?.hiddenHeaderIds.length
      ? orderedMainHeaders.filter(
          (h) => !mainHeaderConfig.hiddenHeaderIds.includes(h.id?.toString() || ''),
        )
      : orderedMainHeaders;

    const laneHeight = layout.theme === 'compact' ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;

    const isVerticalWithSubHeaders =
      layout?.headerDirection === 'vertical' &&
      timelineSubHeaderOptions &&
      subHeaders &&
      subHeaders.length > 0;

    const headerFieldId = getHeaderFieldId(timelineHeaderOptions);
    const itemHeaderPropertyKey = getHeaderItemKey(headerFieldId);
    const subHeaderFieldId = getHeaderFieldId(timelineSubHeaderOptions);
    const itemSubHeaderPropertyKey = getHeaderItemKey(subHeaderFieldId);
    const axis = buildTimelineDateAxis({
      iterationFrom: minimapStart,
      iterationTo: minimapEnd,
      displayFrom: from,
      displayTo: to,
    });
    const timeRanges: TimeRange[] = axis ? flattenTimelineDateAxisToRanges(axis) : [];

    const visibleWindow = {
      from: dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date())),
      to: dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date())),
    };

    const combineMultiSelect = layout.combineMultiSelectHeaders ?? false;
    const itemsMapByPropertyKey = groupItemsByPropertyKey(
      items || [],
      itemHeaderPropertyKey,
      {},
      combineMultiSelect,
    );

    const effectiveMainHeaders = combineMultiSelect
      ? buildCombinedMainHeaders(filteredMainHeaders, itemsMapByPropertyKey)
      : filteredMainHeaders;

    const {
      itemsByPropertyKeyAndDate,
      highestOrderByPropertyKey,
      subRowsByPropertyKey,
      longestItemDurationInDays,
      effectiveSubHeaders: combinedSubHeaders,
    } = timelineSubHeaderOptions && itemSubHeaderPropertyKey && subHeaders
      ? processItemsWithSubHeaders({
          from,
          itemsMapByPropertyKey,
          timeRanges,
          subHeaderPropertyKey: itemSubHeaderPropertyKey,
          subHeaders,
          showEmptyHeaders,
          mainHeaders: effectiveMainHeaders,
          expandedChildren,
          combineMultiSelect,
          itemRanks,
        })
      : processItemsWithoutSubHeaders({
          from,
          itemsMapByPropertyKey,
          timeRanges,
          mainHeaders: effectiveMainHeaders,
          showEmptyHeaders,
          expandedChildren,
          itemRanks,
        });

    const hasSubRows = !!subRowsByPropertyKey;
    const resolvedSubHeaders = combinedSubHeaders ?? subHeaders;
    const subHeaderMap = new Map(resolvedSubHeaders?.map((h) => [h.id?.toString() ?? '', h]));
    const rowChildLanesCache = new Map<number, number>();

    if (isVerticalWithSubHeaders && hasSubRows) {
      const flatRows: TimelineRowType[] = [];

      for (const header of effectiveMainHeaders) {
        const headerId = header.id?.toString() || '';
        const subRowsData = subRowsByPropertyKey[headerId] || [];
        const filteredSubRowsData = applySubRowOrdering(subRowsData, subHeaderConfigs?.[headerId]);

        if (
          !showEmptyHeaders &&
          (filteredSubRowsData.length === 0 ||
            !filteredSubRowsData.some((subRow) =>
              Object.values(subRow.items).some((weekItems) => weekItems.length > 0),
            ))
        ) {
          continue;
        }

        const totalLanes = filteredSubRowsData.reduce((total, subRow) => {
          const { calculatedHeight: subRowHeight } = calculateRowHeightAndMaxDepth(
            subRow.items,
            expandedChildren,
            1,
            rowChildLanesCache,
            visibleWindow,
          );
          return total + subRowHeight;
        }, 0);

        const totalLanesInGroup = Math.max(totalLanes, filteredSubRowsData.length);
        const firstSubRowId = `${headerId}-${filteredSubRowsData[0]?.id}`;

        for (let i = 0; i < filteredSubRowsData.length; i++) {
          const subRowData = filteredSubRowsData[i];
          if (!subRowData) continue;

          const subHeader = subHeaderMap.get(subRowData.id);
          const isFirstSubRow = i === 0;

          if (!subHeader) {
            continue;
          }

          const { calculatedHeight } = calculateRowHeightAndMaxDepth(
            subRowData.items,
            expandedChildren,
            laneHeight,
            rowChildLanesCache,
            visibleWindow,
          );

          flatRows.push({
            id: `${headerId}-${subRowData.id}`,
            label: subHeader.label || subRowData.id,
            selector: subHeader.selector,
            headerColor:
              subHeader.headerColor ||
              lightenTimelineBgColor(header.headerColor, SUBHEADER_OPACITY),
            isDateHeader: false,
            items: subRowData.items,
            allItems: flattenItems(subRowData.items),
            maxOrder: subRowData.maxOrder,
            mainHeaderLabel: header.label,
            mainHeaderDisplayValues: header.displayValues,
            headerId: subHeader.id,
            mainHeaderColor: header.headerColor,
            isFirstSubRow,
            firstSubRowId,
            subRowCount: filteredSubRowsData.length,
            totalLanesInGroup,
            longestItemDurationInDays,
            parentHeaderSelector: header.selector,
            parentHeaderId: header.id,
            calculatedHeight,
          });
        }
      }

      return flatRows;
    }

    const emptyRanges = timeRanges.reduce<{ [key: string]: TimelineItemType[] }>((acc, range) => {
      acc[range.key] = [];
      return acc;
    }, {});

    return effectiveMainHeaders
      .filter((header) => {
        if (showEmptyHeaders) {
          return true;
        }
        const headerId = header.id?.toString() || '';
        if (hasSubRows) {
          const subRows = subRowsByPropertyKey[headerId];
          return subRows?.some((subRow) =>
            Object.values(subRow.items).some((weekItems) => weekItems.length > 0),
          );
        }
        const headerItems = itemsByPropertyKeyAndDate[headerId];
        return headerItems && Object.values(headerItems).some((weekItems) => weekItems.length > 0);
      })
      .map((header) => {
        const headerId = header.id?.toString() || '';
        const maxOrder = highestOrderByPropertyKey[headerId] || 0;

        if (hasSubRows) {
          const subRowsData = subRowsByPropertyKey[headerId] || [];
          const filteredSubRowsData = applySubRowOrdering(
            subRowsData,
            subHeaderConfigs?.[headerId],
          );

          const subRowsWithHeaders: TimelineRowType[] = filteredSubRowsData.map((subRowData) => {
            const subHeader = subHeaderMap.get(subRowData.id);
            const { calculatedHeight } = calculateRowHeightAndMaxDepth(
              subRowData.items,
              expandedChildren,
              laneHeight,
              rowChildLanesCache,
              visibleWindow,
            );

            const allItems = flattenItems(subRowData.items);

            if (subHeader) {
              return {
                ...subHeader,
                id: `${headerId}-${subRowData.id}`,
                label: subHeader.label || subRowData.id,
                selector: subHeader.selector,
                isDateHeader: false,
                items: subRowData.items,
                allItems,
                maxOrder: subRowData.maxOrder,
                headerColor:
                  subHeader.headerColor ||
                  lightenTimelineBgColor(header.headerColor, SUBHEADER_OPACITY),
                mainHeaderColor: header.headerColor,
                longestItemDurationInDays,
                parentHeaderSelector: header.selector,
                headerId: subHeader.id,
                parentHeaderId: header.id,
                calculatedHeight,
              };
            }

            return {
              id: `${headerId}-${subRowData.id}`,
              label: subRowData.id,
              selector: { type: 'CustomPropertySelectorByID', customPropertyID: subRowData.id },
              headerColor: header.headerColor,
              mainHeaderColor: header.headerColor,
              isDateHeader: false,
              items: subRowData.items,
              allItems,
              maxOrder: subRowData.maxOrder,
              longestItemDurationInDays,
              parentHeaderSelector: header.selector,
              headerId: subRowData.id,
              parentHeaderId: header.id,
              calculatedHeight,
            };
          });

          return {
            ...header,
            mainHeaderColor: header.headerColor,
            items: emptyRanges,
            allItems: [],
            maxOrder,
            subRows: subRowsWithHeaders,
            longestItemDurationInDays,
            headerId: header.id,
          };
        }

        const headerItems = itemsByPropertyKeyAndDate[headerId];
        if (!headerItems) {
          return {
            ...header,
            mainHeaderColor: header.headerColor,
            items: emptyRanges,
            allItems: [],
            maxOrder,
            longestItemDurationInDays,
            headerId: header.id,
            calculatedHeight: laneHeight,
          };
        }

        const { calculatedHeight } = calculateRowHeightAndMaxDepth(
          headerItems,
          expandedChildren,
          laneHeight,
          rowChildLanesCache,
          visibleWindow,
        );

        return {
          ...header,
          mainHeaderColor: header.headerColor,
          items: headerItems,
          allItems: flattenItems(headerItems),
          maxOrder,
          longestItemDurationInDays,
          headerId: header.id,
          calculatedHeight,
        };
      });
  }, [
    mainHeaders,
    subHeaders,
    items,
    from,
    to,
    minimapStart,
    minimapEnd,
    timelineHeaderOptions,
    timelineSubHeaderOptions,
    layout,
    showEmptyHeaders,
    expandedChildren,
    mainHeaderConfig,
    subHeaderConfigs,
    itemRanks,
  ]);
};
